import { api } from "./api"
import { DB } from "./db"
import "./types.data"
import "./types.ai"
import { } from './types.blocks'
import { getLocaleByZendesk, isEqual, needsProviderCatalog } from "./helpers"
import { languages } from "./locale"

type Statuses = "initializing" | "ready"

const appPrivateData = {
  status: "initializing" as Statuses,
  servicesLoaded: false,
  articlesLoaded: false,
  boot: false,
}

const defaultBlocks: Block[] = [
  {
    type: "text",
    text: { "en-US": "Welcome" }
  } satisfies BlockText,
] as Block[]


export const app = {

  country: 0,
  googletag: [],
  logo: null as any,
  locale: "", //current locale
  locales: [],
  name: "Country Name",
  defaultLocale: "en-US", //default lang of the country
  regions: [] as Regions[],
  cities: [] as Cities[],
  db: new DB(),

  /**
   * Contact form only. Smart CMS has no ticket-write path, so a site with a
   * `Contact` block still needs these; one without can leave them unset.
   */
  zendeskDomain: "",
  zendeskBrandId: null,

  /**
   * Smart CMS site id (a ULID). Setting it switches to the Smart CMS source
   * and makes `country` unnecessary; left empty, the package uses Directus.
   */
  siteId: "",

  /** No service-map geography published -- a blank map can be correct, so this distinguishes it from a failed load. */
  emptyServiceScope: false,

  state: {
    get status() {
      return appPrivateData.status
    },
    set status(v: Statuses) {
      if (appPrivateData.status === v) return
      appPrivateData.status = v
      app.update()
    },

    info: "Initalizing",

    get servicesLoaded() {
      return appPrivateData.servicesLoaded
    },
    set servicesLoaded(v) {
      if (appPrivateData.servicesLoaded === v) return
      appPrivateData.servicesLoaded = v
      app.update()
    },
    get articlesLoaded() {
      return appPrivateData.articlesLoaded
    },
    set articlesLoaded(v) {
      if (appPrivateData.articlesLoaded === v) return
      appPrivateData.articlesLoaded = v
      app.update()
    },

  },

  page: {
    color: "#000000",
    bgcolor: "#ffffff",

    header: {
      color: null as string,
      bgcolor: null as string,
      menu: [] as Menu[],
    },

    footer: {
      text: {},
      footerlinks: []
    } as BlockFooter,


    content: [...defaultBlocks],

    cookie_banner: null as LocalizableText,

  },

  data: {

    categories: {
      providers: {},
      categories: {},
      subCategories: {},
      populations: {},
      accesibility: {},
    } as Categories,

    services: {} as Services,
    providers: {} as Providers,

    zendesk: {
      categories: {} as { [index: number]: ZendeskCategory },
      sections: {} as { [index: number]: ZendeskSection },
      articles: {} as { [index: number]: ZendeskArticle },
    },
  },


  reactUpdate: null as Function,

  update() {
    console.log("Update App: ", app)
    if (app.reactUpdate) app.reactUpdate()
  },

  async initialize() {

    if (appPrivateData.boot) return
    appPrivateData.boot = true

    console.log("Initializing...")

    // Smart CMS is addressed by a single site id and reads a different backend
    // entirely, so it takes its own path rather than threading conditionals
    // through the Directus flow below.
    if (app.siteId) return initializeSmartCMS()

    const storageCountry = localStorage.getItem("country")
    const storageCategories = localStorage.getItem("categories")

    let c: Country = null
    let cats: Categories = null

    try { c = JSON.parse(storageCountry) } catch (error) { }
    try { cats = JSON.parse(storageCategories) } catch (error) { }

    if (cats) app.data.categories = cats

    if (c) {
      console.log("country exists")
      loadCountry(c)
      app.state.status = "ready"
      app.update()
    }

    const sc = await app.db.loadLocalServices()
    if (needsProviderCatalog(app.page.content)) {
      await app.db.loadLocalProviders()
    }
    const ac = await app.db.loadZendeskContent()
    app.state.servicesLoaded = sc > 0
    app.state.articlesLoaded = ac > 0


    let serverCountry: Country = await api.getCountry(app.country)

    if (serverCountry) {
      if (!c) {
        localStorage.setItem("country", JSON.stringify(serverCountry))
        loadCountry(serverCountry)
      } else {
        if (!isEqual(c, serverCountry)) {
          localStorage.setItem("country", JSON.stringify(serverCountry))
          loadCountry(serverCountry)
          console.log("Country Updated.")
        } else {
          console.log("Country Unchanged")
        }
      }

      app.state.status = "ready"
      app.update()

      if (needsProviderCatalog(app.page.content)) {
        await app.db.updateProviders()
      }
      await app.db.updateServices()
      await app.db.updateArticles()

      app.state.servicesLoaded = true
      app.state.articlesLoaded = true
      app.update()

      console.log("Updating Categories...")
      cats = await api.getCategories()

      if (cats) {
        app.data.categories = cats
        localStorage.setItem("categories", JSON.stringify(cats))
        if (!isEqual(cats, app.data.categories)) {
          app.update()
          console.log("Categories Updated.")
        } else {
          console.log("Categories Unchanged")
        }
      }

      app.update()
      console.log("Initialized")

    }

    // }, 1)

  },


  async getService(id: number) {

  },


}

/**
 * Smart CMS boot path: paint from cache, then refresh from the network, as the
 * Directus flow does. The cache key is namespaced by site id because one
 * origin can serve several sites.
 */
async function initializeSmartCMS() {
  const { loadSmartCMS } = await import("./smartcms")

  const cacheKey = `smartcms:${app.siteId}`
  let cached: Country = null

  try { cached = JSON.parse(localStorage.getItem(cacheKey)) } catch (error) { }

  // Tier 1: paint immediately from whatever is cached locally.
  if (cached) {
    loadCountry(cached)
    app.state.status = "ready"
    app.update()
  }

  const sc = await app.db.loadLocalServices()
  if (needsProviderCatalog(app.page.content)) {
    await app.db.loadLocalProviders()
  }
  const ac = await app.db.loadZendeskContent()
  app.state.servicesLoaded = sc > 0
  app.state.articlesLoaded = ac > 0

  // Tier 2: authoritative read. One call fans out across the Smart CMS tables.
  const payload = await loadSmartCMS(app.siteId, app.locale || undefined)

  if (!payload) {
    // A missing site row is a configuration error, not an empty site. Without
    // a cached copy there is nothing to show, so say so rather than sitting on
    // the splash screen forever.
    console.log(`[smartcms] site ${app.siteId} not found`)
    app.state.info = "Site not found"
    app.state.status = "ready"
    app.update()
    return
  }

  if (!cached || !isEqual(cached, payload.country)) {
    localStorage.setItem(cacheKey, JSON.stringify(payload.country))
    loadCountry(payload.country)
    console.log("Site Config Updated.")
  } else {
    console.log("Site Config Unchanged")
  }

  app.emptyServiceScope = payload.emptyServiceScope
  if (payload.emptyServiceScope) {
    // Legitimate upstream state (empty scope publishes nothing), but the most
    // likely cause of an unexpectedly blank service map -- so it is logged
    // rather than left looking like a failed fetch.
    console.log(`[smartcms] site ${app.siteId} publishes no service-map geography`)
  }

  app.data.services = payload.services
  app.data.providers = payload.providers
  app.data.categories = payload.categories
  app.data.zendesk.categories = payload.articles

  // Flatten the nested tree into the lookup maps the article pages read, the
  // same shape db.updateArticles() produces for the Directus path.
  const sections: { [index: number]: ZendeskSection } = {}
  const articles: { [index: number]: ZendeskArticle } = {}
  for (const category of Object.values(payload.articles)) {
    for (const section of Object.values(category.sections ?? {})) {
      sections[section.id] = section
      for (const article of Object.values(section.articles ?? {})) {
        articles[article.id] = article
      }
    }
  }
  app.data.zendesk.sections = sections
  app.data.zendesk.articles = articles

  // Persist for the next cold start, so tier 1 has something to paint.
  await persistSmartCMS(payload, sections, articles)

  app.state.status = "ready"
  app.state.servicesLoaded = true
  app.state.articlesLoaded = true
  app.update()

  console.log("Initialized (Smart CMS)")
}

/**
 * Writes a payload into the Dexie cache, clearing first: a read is always the
 * complete published set, so a leftover local row was deleted upstream.
 */
async function persistSmartCMS(
  payload: { services: Services; providers: Providers },
  sections: { [index: number]: ZendeskSection },
  articles: { [index: number]: ZendeskArticle },
) {
  try {
    await app.db.services.clear()
    for (const service of Object.values(payload.services)) {
      await app.db.services.put(service, service.id)
    }

    await app.db.providers.clear()
    for (const provider of Object.values(payload.providers)) {
      await app.db.providers.put(provider, provider.id)
    }

    await app.db.zendeskCategories.clear()
    for (const category of Object.values(app.data.zendesk.categories)) {
      await app.db.zendeskCategories.put(category, category.id)
    }

    await app.db.zendeskSections.clear()
    for (const section of Object.values(sections)) {
      await app.db.zendeskSections.put(section, section.id)
    }

    await app.db.articles.clear()
    for (const article of Object.values(articles)) {
      await app.db.articles.put(article, article.id)
    }
  } catch (error) {
    // A failed cache write costs a slower next boot, not a broken page.
    console.log("[smartcms] error caching content", error)
  }
}

function loadCountry(c: Country) {
  if (!c) return
  const locale = location.pathname.split('/')[1]
  const validLocale = Object.keys(languages).map(locale => locale.toLowerCase()).includes(locale.toLowerCase())
  /*
   * `app.country` stays 0 here. Components branch on specific Directus ids
   * (42 and 43 change the header, footer and map layout), so propagating a
   * migrated site's `oldid` would make it inherit another site's layout.
   */
  if (!app.siteId) app.country = c.id
  app.name = c.name
  // A CMS logo wins over the bundled asset, so branding changes without a
  // redeploy. Directus sites leave it undefined.
  if (c.logo) app.logo = c.logo
  app.locale ||= validLocale ? getLocaleByZendesk(locale.toLowerCase()) : c.locale
  app.locales = c.languages
  app.page.content = c.content || []
  app.defaultLocale = c.locale

  app.page.color = c.pagecolor || "#000000"
  app.page.bgcolor = c.pagebgcolor || "#ffffff"

  app.page.header.color = c.headercolor || c.pagecolor
  app.page.header.bgcolor = c.headerbgcolor || c.pagebgcolor
  app.page.header.menu = c.menu || []
  app.page.cookie_banner = c.cookie_banner || null
  app.regions = c.regions
  app.cities = c.cities
  app.googletag = c.googletag

  const footer = c.content.find(b => b.type === "footer")
  if (footer) app.page.footer = footer as BlockFooter
}

export function translate(t: LocalizableContent): string {
  if (!t) return ""
  if (typeof t === "string") return t
  if (typeof t === "object") return t[app.locale] || t[app.defaultLocale] || ""
  return ""
}


export async function sleep(ms = 1000) {
  return new Promise(a => setTimeout(a, ms))
}